import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon, GlobeIcon, InfoIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { useAdminData } from '../../contexts/AdminDataContext';
export function Settings() {
  const {
    settings,
    settingsLoading,
    settingsAreGlobal,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setSaved(false);
        setError('');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-598" data-mp="2iv45dylbme2z">
      <p className="text-sm text-ink-muted" data-id="element-599" data-mp="2esqz992nwe5o">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-600" data-mp="2ssibb49mbkh1">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-601" data-mp="2nuqjsbcvy0dd">
        Oplysningerne herunder er hjemmesidens ene fælles kilde. De bruges automatisk i topbjælken,
        hero-sektionen, kontaktafsnittet og footeren — du retter dem kun ét sted.
      </p>

      {settingsAreGlobal ? <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-602" data-mp="3bk2ph1907qax">
          <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-603" data-mp="3lfw9t6syso39" />
          Ændringer gemmes på serveren og vises for alle besøgende på alle enheder.
        </p> : <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-604" data-mp="3nkj880p3c2lw">
          <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-605" data-mp="3sk46e8wfs6xk" />
          <span data-id="element-606" data-mp="1c03darrda4k">
            Der er endnu ikke tilsluttet en server, så ændringer gemmes indtil videre kun i denne browser.
            Indsæt adressen på jeres backend i <code className="text-ink" data-id="element-607" data-mp="b2uscmwe2ntp">utils/settingsApi.ts</code>{' '}
            (SETTINGS_ENDPOINT), så gemmes og hentes oplysningerne globalt for alle besøgende.
          </span>
        </p>}

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      const result = await saveSettings(draft);
      setSaving(false);
      if (result.ok) {
        setSaved(true);
        setError('');
      } else {
        setSaved(false);
        setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      }
    }} data-id="element-608" data-mp="k3j4td64wg4h">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-609" data-mp="2jcaqksg0324n">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-610" data-mp="2eig79q4b4p63">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-611" data-mp="29l6crw229nbe">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-612" data-mp="24mcuzrfqra5f" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-613" data-mp="329256oklkt2d" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-614" data-mp="2xa8ybeztg0b6" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-615" data-mp="2sa4kxxu7flp6">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-616" data-mp="2nckv00o59khe">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-617" data-mp="1gwim1kyjm5qg">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-618" data-mp="1gtmkznref0xs" />
            <TextField id="in-mail" label="E-mail" type="email" {...field('email')} data-id="element-619" data-mp="35iv9na4eyuku" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-620" data-mp="2qtp31138y22x" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-621" data-mp="2vr7aso5j2b9c" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-622" data-mp="3jicqn3bgx3jn">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-623" data-mp="3ofd0lpbfuqg1">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-624" data-mp="39ilyv9eal2n9">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-625" data-mp="3ei8chjy18kwl" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-626" data-mp="1y3qjtmbqgz66" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-627" data-mp="l56tuwhmq7cn">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-628" data-mp="u64myjqj4ugg">
          <Button type="submit" size="lg" disabled={!dirty || saving || settingsLoading} data-id="element-629" data-mp="u5lfjwyi9ogo">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-630" data-mp="2eyvsssse73b" />
                Gemmer …
              </> : 'Gem ændringer'}
          </Button>

          {settingsLoading && <p className="text-[15px] text-ink-muted" data-id="element-631" data-mp="b7shb7ytu2pm">Henter aktuelle oplysninger …</p>}

          {dirty && !saving && !saved && !error && <p className="text-[15px] text-ink-muted" data-id="element-632" data-mp="6epiakba6121">Der er ændringer, der ikke er gemt.</p>}

          {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-633" data-mp="3ava5fvehhgo2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-634" data-mp="1due23ath7xj0">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-635" data-mp="1isz6lmfrh3d9" />
              </span>
              Ændringerne er gemt og opdateret på hjemmesiden
            </p>}

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-636" data-mp="1sjjyys4udbnq">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-637" data-mp="uw1rrlqtwhlu" />
              {error}
            </p>}
        </div>
      </form>

      <SecuritySection data-id="element-638" data-mp="zwph82zesqdy" />
    </div>;
}